/**
 * VIN Decoder Service using NHTSA vPIC API
 * Documentation: https://vpic.nhtsa.dot.gov/api/
 */

const NHTSA_API_BASE = 'https://vpic.nhtsa.dot.gov/api/vehicles';

class VinDecoderService {
  /**
   * Decode a VIN using NHTSA API
   * @param {string} vin - Vehicle Identification Number
   * @param {number} modelYear - Optional model year for better accuracy
   * @returns {Object} Decoded vehicle information
   */
  async decodeVIN(vin, modelYear = null) {
    try {
      // Clean the VIN
      const cleanVIN = vin.replace(/[^A-HJ-NPR-Z0-9]/gi, '').toUpperCase();
      
      if (cleanVIN.length !== 17) {
        return {
          error: true,
          message: 'VIN must be exactly 17 characters',
          data: null
        };
      }

      // Build API URL
      let url = `${NHTSA_API_BASE}/DecodeVinValues/${cleanVIN}?format=json`;
      if (modelYear) {
        url += `&modelyear=${modelYear}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (!data.Results || data.Results.length === 0) {
        return {
          error: true,
          message: 'No data found for this VIN',
          data: null
        };
      }

      const result = data.Results[0];
      
      // Check for errors in the API response
      // NHTSA ErrorCode is a comma-separated string, e.g. '0', '1', '1,6', '5,11'
      // Code 0 = No error, 1 = Check digit wrong, 4 = Invalid VIN, 5+ = Informational
      // Codes 1-3 still return valid data — only code 4 means the VIN is truly invalid
      const errorCodes = (result.ErrorCode || '0')
        .split(',')
        .map(c => parseInt(c.trim(), 10))
        .filter(c => !isNaN(c));
      const hasFatalError = errorCodes.includes(4);
      const hasWarnings = errorCodes.some(c => c > 0);
      
      if (hasFatalError) {
        return {
          error: true,
          message: result.ErrorText || 'Invalid VIN',
          data: null
        };
      }

      // Transform NHTSA data to our vehicle format
      const vehicleData = this.transformNHTSAData(result);
      
      // Build message with warnings if present
      let message = 'VIN decoded successfully';
      if (hasWarnings) {
        message += `. Warning: ${result.ErrorText || 'Model year may not match VIN year'}`;
      }
      
      return {
        error: false,
        message: message,
        data: vehicleData,
        warnings: hasWarnings ? result.ErrorText : null,
        errorCodes: errorCodes,
        raw: result // Include raw data for reference
      };
    } catch (error) {
      console.error('Error decoding VIN:', error);
      return {
        error: true,
        message: 'Failed to decode VIN. Please try again.',
        data: null
      };
    }
  }

  /**
   * Transform NHTSA API data to match our vehicle data structure
   * @param {Object} nhtsa - Raw NHTSA API response
   * @returns {Object} Transformed vehicle data
   */
  transformNHTSAData(nhtsa) {
    // Extract and clean data from NHTSA response
    const vehicleData = {
      vin: nhtsa.VIN,
      year: parseInt(nhtsa.ModelYear) || null,
      make: nhtsa.Make || '',
      model: nhtsa.Model || '',
      trim: nhtsa.Trim || '',
      bodyType: nhtsa.BodyClass || '',
      doors: parseInt(nhtsa.Doors) || null,
      engineCylinders: parseInt(nhtsa.EngineCylinders) || null,
      engineLiters: parseFloat(nhtsa.DisplacementL) || null,
      engineHP: parseFloat(nhtsa.EngineHP) || null,
      fuelType: this.mapFuelType(nhtsa.FuelTypePrimary),
      transmission: this.mapTransmission(nhtsa.TransmissionStyle),
      drivetrain: this.mapDrivetrain(nhtsa.DriveType),
      gvwr: nhtsa.GVWR || '',
      vehicleType: this.mapVehicleType(nhtsa.VehicleType, nhtsa.BodyClass),
      manufacturerName: nhtsa.Manufacturer || '',
      plantCity: nhtsa.PlantCity || '',
      plantState: nhtsa.PlantState || '',
      plantCountry: nhtsa.PlantCountry || '',
      
      // Safety features
      abs: nhtsa.ABS === 'Standard' || nhtsa.ABS === 'Optional',
      esc: nhtsa.ESC === 'Standard' || nhtsa.ESC === 'Optional',
      tractionControl: nhtsa.TractionControl === 'Standard' || nhtsa.TractionControl === 'Optional',
      
      // Additional details
      series: nhtsa.Series || '',
      engineModel: nhtsa.EngineModel || '',
      engineConfiguration: nhtsa.EngineConfiguration || '',
      turbo: nhtsa.Turbo === 'Yes',
      seats: parseInt(nhtsa.SeatBeltsAll) || null,
      wheelbase: parseFloat(nhtsa.WheelBaseShortInches) || null,
      
      // Error checking
      errorCode: nhtsa.ErrorCode,
      errorText: nhtsa.ErrorText,
      possibleValues: nhtsa.PossibleValuesForErrorCode || ''
    };

    // Build a description from available data
    vehicleData.description = this.buildDescription(vehicleData, nhtsa);

    return vehicleData;
  }

  /**
   * Map NHTSA fuel type to our system
   */
  mapFuelType(fuel) {
    if (!fuel) return 'Gasoline';
    
    const fuelMap = {
      'Gasoline': 'Gasoline',
      'Diesel': 'Diesel',
      'Electric': 'Electric',
      'Hybrid': 'Hybrid',
      'Plug-In Hybrid': 'Plug-In Hybrid',
      'Hydrogen': 'Hydrogen',
      'Flexible Fuel': 'Flex Fuel',
      'Compressed Natural Gas': 'CNG',
      'Liquefied Natural Gas': 'LNG',
      'Liquefied Petroleum Gas': 'LPG'
    };
    
    return fuelMap[fuel] || fuel;
  }

  /**
   * Map NHTSA transmission to our system
   */
  mapTransmission(transmission) {
    if (!transmission) return 'Automatic';
    
    if (transmission.toLowerCase().includes('automatic')) {
      return 'Automatic';
    } else if (transmission.toLowerCase().includes('manual')) {
      return 'Manual';
    } else if (transmission.toLowerCase().includes('cvt')) {
      return 'CVT';
    }
    
    return transmission;
  }

  /**
   * Map NHTSA drive type to our system
   */
  mapDrivetrain(driveType) {
    if (!driveType) return 'FWD';
    
    const driveMap = {
      'Front-Wheel Drive': 'FWD',
      'FWD': 'FWD',
      'Rear-Wheel Drive': 'RWD',
      'RWD': 'RWD',
      'All-Wheel Drive': 'AWD',
      'AWD': 'AWD',
      'Four-Wheel Drive': '4WD',
      '4WD': '4WD',
      '4x4': '4WD'
    };
    
    return driveMap[driveType] || driveType;
  }

  /**
   * Map vehicle type based on NHTSA data
   */
  mapVehicleType(vehicleType, bodyClass) {
    // Check if it's a golf cart first
    if (vehicleType && vehicleType.toLowerCase().includes('golf')) {
      return 'golf-cart';
    }
    if (bodyClass && bodyClass.toLowerCase().includes('golf')) {
      return 'golf-cart';
    }
    
    // For regular vehicles
    if (vehicleType && vehicleType.toLowerCase().includes('motorcycle')) {
      return 'motorcycle';
    }
    
    return 'vehicle';
  }

  /**
   * Build a description from decoded data
   */
  buildDescription(vehicleData, nhtsa) {
    const parts = [];
    
    // Basic vehicle info
    if (vehicleData.year && vehicleData.make && vehicleData.model) {
      parts.push(`This ${vehicleData.year} ${vehicleData.make} ${vehicleData.model}`);
    }
    
    // Trim and body type
    if (vehicleData.trim) {
      parts.push(`${vehicleData.trim} trim`);
    }
    if (vehicleData.bodyType) {
      parts.push(`is a ${vehicleData.bodyType}`);
    }
    
    // Engine info
    if (vehicleData.engineLiters && vehicleData.engineCylinders) {
      parts.push(`with a ${vehicleData.engineLiters}L ${vehicleData.engineCylinders}-cylinder engine`);
    } else if (vehicleData.engineCylinders) {
      parts.push(`with a ${vehicleData.engineCylinders}-cylinder engine`);
    }
    
    // Power
    if (vehicleData.engineHP) {
      parts.push(`producing ${vehicleData.engineHP} HP`);
    }
    
    // Transmission and drive
    if (vehicleData.transmission || vehicleData.drivetrain) {
      const transDesc = [];
      if (vehicleData.transmission) transDesc.push(vehicleData.transmission);
      if (vehicleData.drivetrain) transDesc.push(vehicleData.drivetrain);
      parts.push(`featuring ${transDesc.join(' ')}`);
    }
    
    // Fuel type
    if (vehicleData.fuelType && vehicleData.fuelType !== 'Gasoline') {
      parts.push(`runs on ${vehicleData.fuelType}`);
    }
    
    // Manufacturing info
    if (vehicleData.plantCity && vehicleData.plantCountry) {
      parts.push(`manufactured in ${vehicleData.plantCity}, ${vehicleData.plantCountry}`);
    }
    
    return parts.join('. ') + '.';
  }

  /**
   * Get all makes from NHTSA
   */
  async getAllMakes() {
    try {
      const response = await fetch(`${NHTSA_API_BASE}/GetAllMakes?format=json`);
      const data = await response.json();
      
      return {
        error: false,
        data: data.Results.map(make => ({
          id: make.Make_ID,
          name: make.Make_Name
        }))
      };
    } catch (error) {
      console.error('Error fetching makes:', error);
      return {
        error: true,
        data: []
      };
    }
  }

  /**
   * Get models for a specific make
   */
  async getModelsForMake(make, year = null) {
    try {
      let url = `${NHTSA_API_BASE}/GetModelsForMake/${encodeURIComponent(make)}?format=json`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      return {
        error: false,
        data: data.Results.map(model => ({
          id: model.Model_ID,
          name: model.Model_Name
        }))
      };
    } catch (error) {
      console.error('Error fetching models:', error);
      return {
        error: true,
        data: []
      };
    }
  }

  /**
   * Validate a VIN checksum
   */
  validateVIN(vin) {
    // Clean VIN
    const cleanVIN = vin.replace(/[^A-HJ-NPR-Z0-9]/gi, '').toUpperCase();
    
    if (cleanVIN.length !== 17) {
      return false;
    }
    
    // VIN character weights
    const weights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
    
    // Character values
    const values = {
      'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 6, 'G': 7, 'H': 8,
      'J': 1, 'K': 2, 'L': 3, 'M': 4, 'N': 5, 'P': 7, 'R': 9,
      'S': 2, 'T': 3, 'U': 4, 'V': 5, 'W': 6, 'X': 7, 'Y': 8, 'Z': 9,
      '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9
    };
    
    let sum = 0;
    for (let i = 0; i < 17; i++) {
      const char = cleanVIN[i];
      const value = values[char];
      if (value === undefined) return false;
      sum += value * weights[i];
    }
    
    const checkDigit = cleanVIN[8];
    const calculatedCheck = sum % 11;
    const expectedCheck = calculatedCheck === 10 ? 'X' : calculatedCheck.toString();
    
    return checkDigit === expectedCheck;
  }
}

export default new VinDecoderService();
